import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast } from "@jsq/ds/core";
import { FormTextInput, FormCheckbox, SubmitButton } from "@jsq/ds/forms";
import { useLinkEpAndIpdForTransactionMutation } from "~/graphql/supergraphHooks";
import { getErrorMessage } from "~/utils/errors";
import * as styles from "./styles_vanilla.css";

interface FormValues {
  transactionId: string;
  epProviderId: string;
  ipdProviderId: string;
  startSourceOfFundsRuleCheckOperation: boolean;
}

const validationSchema = Yup.object().shape({
  transactionId: Yup.string().required("Transaction ID is required"),
  epProviderId: Yup.string().required("EP Provider ID is required"),
  ipdProviderId: Yup.string().required("IPD Provider ID is required"),
  startSourceOfFundsRuleCheckOperation: Yup.boolean(),
});

const initialValues: FormValues = {
  transactionId: "",
  epProviderId: "",
  ipdProviderId: "",
  startSourceOfFundsRuleCheckOperation: false,
};

const LinkEpAndIpd: React.FC = () => {
  const [result, setResult] = useState<string | null>(null);

  const [linkEpAndIpdForTransaction, { loading }] = useLinkEpAndIpdForTransactionMutation({
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleSubmit = async (values: FormValues) => {
    const response = await linkEpAndIpdForTransaction({
      variables: {
        input: {
          transactionId: values.transactionId,
          epProviderId: values.epProviderId,
          ipdProviderId: values.ipdProviderId,
          startSourceOfFundsRuleCheckOperation: values.startSourceOfFundsRuleCheckOperation,
        },
      },
    });

    if (response.data?.linkEpAndIpdForTransaction) {
      setResult(response.data.linkEpAndIpdForTransaction);
      toast.success("Successfully linked EP and IPD for transaction");
    }
  };

  return (
    <Flex
      data-testid="link-ep-and-ipd-root"
      flexDirection="column"
      className={styles.container}
    >
      <div className={styles.header}>
        <Text variant="regular-xxxl" className={styles.pageTitle}>
          Manual reconciliation
        </Text>
      </div>
      <div className={styles.contentArea} data-testid="link-ep-and-ipd-content">
        <div className={styles.formSection}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <div className={styles.formFields}>
                <FormTextInput
                  name="transactionId"
                  label="Transaction ID"
                  placeholder="Enter transaction UUID (lowercase)"
                  helpText="The UUID of the transaction to link"
                  required
                />
                <FormTextInput
                  name="epProviderId"
                  label="EP Provider ID"
                  placeholder="Enter expected payment provider UUID (lowercase)"
                  helpText="The UUID of the expected payment provider"
                  required
                />
                <FormTextInput
                  name="ipdProviderId"
                  label="IPD Provider ID"
                  placeholder="Enter incoming payment detail provider UUID (lowercase)"
                  helpText="The UUID of the incoming payment detail provider"
                  required
                />
                <FormCheckbox
                  name="startSourceOfFundsRuleCheckOperation"
                  label="Start Source of Funds Rule Check Operation"
                />
              </div>
              <div className={styles.submitSection}>
                <SubmitButton isLoading={loading}>Link EP and IPD</SubmitButton>
              </div>
            </Form>
          </Formik>
        </div>
        {result && (
          <div className={styles.resultSection}>
            <Text variant="regular-lg" className={styles.resultTitle}>
              Result
            </Text>
            <div className={styles.resultContent}>
              <Text variant="regular-md">{result}</Text>
            </div>
          </div>
        )}
      </div>
    </Flex>
  );
};

export default LinkEpAndIpd;
export { LinkEpAndIpd };
